import * as React from 'react';
import { createAppSettingsRouteWithSettingType } from '../../helpers/routeHelpers';
import { AppPlatform, ReleaseStepType, SettingType } from '@/types';
import {
    SettingFilled,
    AppstoreFilled,
    HeartFilled,
    MobileFilled,
    SkinFilled,
    ThunderboltFilled,
    CalendarFilled,
    NotificationFilled,
    ControlFilled,
    CheckSquareFilled,
    FileTextFilled,
    CompassFilled,
    RocketFilled,
    GoldFilled,
} from '@ant-design/icons';

export interface MenuItem {
    id: string;
    label: string;
    path: string;
    icon: () => JSX.Element;
    showOnlyOnPlatforms?: AppPlatform[];
    subroute?: (orgId: string, appId: string) => string;
    isRunwayAdminRouteOnly?: boolean;
    minimumRequiredMigratedWorkflowStepsToShow?: ReleaseStepType[];
    minimumRequiredMigratedWorkflowStepsToHide?: ReleaseStepType[];
}

export const settingsMenuItems: MenuItem[] = [
    {
        id: 'general',
        label: 'General',
        path: `/${SettingType.General}`,
        icon: () => <SettingFilled />,
    },
    {
        id: 'workflowSteps',
        label: 'Workflow steps',
        path: `/${SettingType.WorkflowSteps}`,
        icon: () => <GoldFilled />,
        minimumRequiredMigratedWorkflowStepsToShow: [ReleaseStepType.BetaTesting],
    },
    {
        id: 'team',
        label: 'Team',
        path: `/${SettingType.Team}`,
        icon: () => <SkinFilled />,
    },
    {
        id: 'releasePilotRotation',
        label: 'Release pilot rotation',
        path: `/${SettingType.ReleasePilotRotation}`,
        icon: () => <CompassFilled />,
    },
    {
        id: 'integrations',
        label: 'Integrations',
        path: `/${SettingType.Integrations}`,
        icon: () => <AppstoreFilled />,
    },
    {
        id: 'profilesAndDevices',
        label: 'Profiles and devices',
        path: `/${SettingType.ProfilesAndDevices}`,
        icon: () => <MobileFilled />,
        showOnlyOnPlatforms: [AppPlatform.Ios],
    },
    {
        id: 'signingKeys',
        label: 'Signing keys',
        path: `/${SettingType.SigningKeys}`,
        icon: () => <MobileFilled />,
        showOnlyOnPlatforms: [AppPlatform.Android],
    },
    {
        id: 'healthMetrics',
        label: 'Health metrics',
        path: `/${SettingType.HealthMetrics}`,
        icon: () => <HeartFilled />,
    },
    {
        id: 'automations',
        label: 'Automations',
        path: `/${SettingType.Automations}`,
        icon: () => <ThunderboltFilled />,
    },
    {
        id: 'notifications',
        label: 'Notifications',
        path: `/${SettingType.Notifications}`,
        icon: () => <NotificationFilled />,
    },
    {
        id: 'schedule',
        label: 'Schedule',
        path: `/${SettingType.Schedule}`,
        icon: () => <CalendarFilled />,
        subroute: (orgId, appId) => createAppSettingsRouteWithSettingType(orgId, appId, SettingType.ScheduleCadence),
    },
    {
        id: 'app-store',
        label: 'App store defaults',
        path: `/${SettingType.AppStore}`,
        icon: () => <ControlFilled />,
        showOnlyOnPlatforms: [
            AppPlatform.Android,
            AppPlatform.Ios,
            AppPlatform.VisionOS,
            AppPlatform.TvOS,
            AppPlatform.WearOS,
        ],
        subroute: (orgId, appId) =>
            createAppSettingsRouteWithSettingType(orgId, appId, SettingType.DefaultReleaseNotes),
        minimumRequiredMigratedWorkflowStepsToHide: [ReleaseStepType.Metadata],
    },
    {
        id: 'checklists',
        label: 'Checklists',
        path: `/${SettingType.Checklists}`,
        icon: () => <CheckSquareFilled />,
    },
    {
        id: 'custom-strings',
        label: 'Custom strings',
        path: `/${SettingType.CustomStrings}`,
        icon: () => <FileTextFilled />,
    },
    {
        id: 'setup-tasks',
        label: 'Setup tasks',
        path: `/${SettingType.SetupTasks}`,
        icon: () => <RocketFilled />,
    },
];
